import React, { useState, useMemo, useRef, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Grid,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  FormControlLabel,
  ButtonGroup,
  Stack,
  Collapse,
} from '@mui/material'
import type { PaperProps } from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  School as SchoolIcon,
  WorkOutline as WorkOutlineIcon,
  PictureAsPdf as PdfIcon,
  History as HistoryIcon,
  Block as BlockIcon,
} from '@mui/icons-material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { extractErrorMessage, normalizeError, getFieldErrorMap } from '../../utils/errorHandler'
import { formatDateTimeJST } from '../../utils/dateUtils'
import SkillStatsSection from '../../components/Skills/SkillStatsSection'
import RequiredBadge from '../../components/Common/RequiredBadge'

// 日付(DBのDATE/ISO文字列)を年月入力(type="month", "YYYY-MM")用に変換
const toMonthInput = (dateString?: string | null): string => {
  if (!dateString) return ''
  return dateString.split('T')[0].substring(0, 7)
}

// 年月入力("YYYY-MM")をDB保存用の月初日("YYYY-MM-01")に変換。空はnull
const toMonthFirstDay = (monthString?: string | null): string | null => {
  if (!monthString) return null
  return `${monthString.substring(0, 7)}-01`
}

// タイトルバーを掴んで移動できるドラッグ可能なダイアログ用Paper
// （新規依存を追加せずポインタイベントで実装。.draggable-dialog-handle を掴んで移動）
function DraggablePaper(props: PaperProps) {
  const [pos, setPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 })

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement
    if (!target.closest('.draggable-dialog-handle')) return
    const startX = e.clientX
    const startY = e.clientY
    const origX = pos.x
    const origY = pos.y
    const handleMove = (ev: MouseEvent) => {
      setPos({ x: origX + (ev.clientX - startX), y: origY + (ev.clientY - startY) })
    }
    const handleUp = () => {
      document.removeEventListener('mousemove', handleMove)
      document.removeEventListener('mouseup', handleUp)
    }
    document.addEventListener('mousemove', handleMove)
    document.addEventListener('mouseup', handleUp)
  }

  return (
    <Paper
      {...props}
      onMouseDown={handleMouseDown}
      style={{ ...props.style, transform: `translate(${pos.x}px, ${pos.y}px)` }}
    />
  )
}

interface SkillCategory {
  category_id: string
  category_name: string
  display_order: number
}

interface SkillItem {
  item_id: string
  category_id: string
  category_name: string
  item_name: string
  item_description: string
}

interface EmployeeSkill {
  id?: number
  employee_id?: string
  item_id: string
  item_name?: string
  category_id?: string
  category_name?: string
  skill_level: 'S' | 'A' | 'B' | 'C' | 'D' | ''
  comment: string | null
  status?: 'draft' | 'pending_approval' | 'approved' | 'rejected'
  approved_by?: string | null
  approved_by_name?: string
  approved_at?: string | null
  rejected_reason?: string | null
  created_at?: string
  updated_at?: string
}

interface SkillLevelDefinition {
  id?: number
  category_id?: string | null
  level: string
  level_name: string
  level_description: string
  achievement_criteria: string
  display_order: number
  is_default?: boolean
}

interface Certification {
  certification_id: string
  certification_name: string
  issuing_organization: string
  has_expiry: boolean
  expiry_months: number | null
  display_order: number
  is_active: boolean
}

interface EmployeeCertification {
  id?: number
  employee_id?: string
  certification_id?: string
  certification_name: string
  issuing_organization?: string
  acquired_date: string
  expiry_date?: string | null
  certification_number?: string
  status?: 'draft' | 'pending_approval' | 'approved' | 'rejected'
  approved_by?: string | null
  approved_by_name?: string
  approved_at?: string | null
  rejected_reason?: string | null
  created_at?: string
  updated_at?: string
  is_custom?: boolean
}

interface EmployeeProject {
  id?: number
  employee_id?: string
  project_name: string
  start_date: string
  end_date: string | null
  role: string | null  // カンマ区切りの役割リスト
  responsibilities: string | null
  technologies: string | null
  project_description: string | null
  status?: 'draft' | 'pending_approval' | 'approved' | 'rejected'
  approved_by?: string | null
  approved_by_name?: string
  approved_at?: string | null
  rejected_reason?: string | null
  created_at?: string
  updated_at?: string
}

interface RejectionHistoryEntry {
  id: number
  rejected_reason: string | null
  rejected_by: string | null
  rejected_by_name: string | null
  rejected_at: string
}

// IT業界の役割リスト
const PROJECT_ROLES = [
  'プロジェクトマネージャー',
  'プロジェクトリーダー',
  'アカウントマネージャー',
  '業務コンサルタント',
  'ITコンサルタント',
  'システムアーキテクト',
  'テックリード',
  'スクラムマスター',
  'プロダクトオーナー',
  'フロントエンドエンジニア',
  'バックエンドエンジニア',
  'フルスタックエンジニア',
  'インフラエンジニア',
  'DevOpsエンジニア',
  'QAエンジニア',
  'テストエンジニア',
  'データエンジニア',
  'データサイエンティスト',
  'UIUXデザイナー',
  'ビジネスアナリスト',
] as const

const getSkillLevelColor = (level: string) => {
  switch (level) {
    case 'S': return '#9c27b0'
    case 'A': return '#2196f3'
    case 'B': return '#4caf50'
    case 'C': return '#ff9800'
    case 'D': return '#9e9e9e'
    default: return '#e0e0e0'
  }
}

const MySkillsPage: React.FC = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()

  // URLパラメータからタブの初期値を取得（デフォルトは0）
  const getInitialTab = () => {
    const tab = searchParams.get('tab')
    if (tab === 'skills' || tab === '0') return 0
    if (tab === 'certifications' || tab === '1') return 1
    if (tab === 'projects' || tab === '2') return 2
    return 0
  }

  const [tabValue, setTabValue] = useState(getInitialTab())
  const [expandedCategories, setExpandedCategories] = useState<string[]>([])
  const [skillData, setSkillData] = useState<Record<string, EmployeeSkill>>({})
  const [editableSkills, setEditableSkills] = useState<Record<string, boolean>>({})
  const [originalSkillData, setOriginalSkillData] = useState<Record<string, EmployeeSkill>>({})
  const [editedSkillIds, setEditedSkillIds] = useState<Set<string>>(new Set())

  // タブ変更時にURLパラメータを更新
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
    const tabNames = ['skills', 'certifications', 'projects']
    setSearchParams({ tab: tabNames[newValue] })
  }

  // 履歴書PDFダウンロード
  const handleDownloadResume = async () => {
    try {
      const employeeId = user?.employee_id

      if (!employeeId) {
        toast.error('ユーザー情報が取得できません')
        return
      }

      const response = await api.get(`/skills/resume/${employeeId}/pdf`, {
        responseType: 'blob'
      })

      // PDFをダウンロード
      const blob = response.data
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `resume_${employeeId}_${Date.now()}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('履歴書をダウンロードしました')
    } catch (error) {
      console.error('履歴書ダウンロードエラー:', error)
      toast.error(extractErrorMessage(error, '履歴書のダウンロードに失敗しました'))
    }
  }

  // スキルタブのstate
  const [filterType, setFilterType] = useState<'all' | 'unentered' | 'pending'>('all')
  const [commentModalOpen, setCommentModalOpen] = useState(false)
  const [currentEditingItem, setCurrentEditingItem] = useState<string | null>(null)
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  // 資格タブのstate
  const [isCertDialogOpen, setIsCertDialogOpen] = useState(false)
  const [editingCertification, setEditingCertification] = useState<EmployeeCertification | null>(null)
  const [certFormData, setCertFormData] = useState<EmployeeCertification>({
    certification_name: '',
    acquired_date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
    expiry_date: null,
    certification_number: '',
    issuing_organization: '',
    is_custom: false
  })
  const [isCustomCert, setIsCustomCert] = useState(false)
  // 資格フォームのフィールド別エラー（フォームUX_バリデーション共通設計方針 §4, §6）
  const [certErrors, setCertErrors] = useState<Record<string, string>>({})

  // サーバのバリデーションエラーをフィールド直下へ反映し、トーストは要約のみ表示する
  const applyCertApiError = (error: unknown, defaultMessage: string) => {
    const fieldMap = getFieldErrorMap(error)
    if (Object.keys(fieldMap).length > 0) {
      setCertErrors(fieldMap)
      toast.error(normalizeError(error, defaultMessage).message)
    } else {
      toast.error(extractErrorMessage(error, defaultMessage))
    }
  }

  // プロジェクト歴タブのstate
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false)
  const [editingProject, setEditingProject] = useState<EmployeeProject | null>(null)
  const [projectFormData, setProjectFormData] = useState<EmployeeProject>({
    project_name: '',
    start_date: '',
    end_date: null,
    role: '',
    responsibilities: '',
    technologies: '',
    project_description: '',
  })
  // プロジェクト歴フォームのフィールド別エラー（フォームUX_バリデーション共通設計方針 §4, §6）
  const [projectErrors, setProjectErrors] = useState<Record<string, string>>({})

  // サーバのバリデーションエラーをフィールド直下へ反映し、トーストは要約のみ表示する
  const applyProjectApiError = (error: unknown, defaultMessage: string) => {
    const fieldMap = getFieldErrorMap(error)
    if (Object.keys(fieldMap).length > 0) {
      setProjectErrors(fieldMap)
      toast.error(normalizeError(error, defaultMessage).message)
    } else {
      toast.error(extractErrorMessage(error, defaultMessage))
    }
  }

  // ==================== スキル関連のAPI ====================

  // カテゴリ一覧取得
  const { data: categoriesData } = useQuery({
    queryKey: ['skill-categories'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: SkillCategory[] }>('/skills/categories?is_active=true')
      return response.data.data
    },
  })

  // スキル項目一覧取得（全カテゴリ）
  const { data: skillItemsData, isLoading: skillItemsLoading } = useQuery({
    queryKey: ['skill-items-all'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: { items: SkillItem[] } }>('/skills/items?is_active=true&limit=1000')
      return response.data.data.items
    },
  })

  // 資格マスタ一覧取得
  const { data: certificationMasterData } = useQuery({
    queryKey: ['certification-master'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: Certification[] }>('/skills/certifications?is_active=true')
      return response.data.data
    },
  })

  // カテゴリ別レベル定義を取得
  const { data: levelDefinitionsMap } = useQuery({
    queryKey: ['skill-level-definitions-all', categoriesData],
    queryFn: async () => {
      if (!categoriesData) return {}
      const definitionsMap: Record<string, SkillLevelDefinition[]> = {}

      // デフォルト定義を取得
      const defaultResponse = await api.get<{ success: boolean; data: SkillLevelDefinition[] }>('/skills/level-definitions')
      definitionsMap['default'] = defaultResponse.data.data

      // 各カテゴリのレベル定義を取得（並列処理）
      const promises = categoriesData.map(async (category) => {
        const response = await api.get<{ success: boolean; data: SkillLevelDefinition[] }>(
          `/skills/level-definitions?category_id=${category.category_id}`
        )
        definitionsMap[category.category_id] = response.data.data
      })

      await Promise.all(promises)
      return definitionsMap
    },
    enabled: !!categoriesData,
  })

  // 自分のスキル情報一覧取得
  const { data: mySkillsData, refetch: refetchMySkills, isLoading: mySkillsLoading } = useQuery({
    queryKey: ['my-skills'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: EmployeeSkill[] }>('/skills/employee-skills')
      return response.data.data
    },
  })

  // mySkillsDataが更新されたらskillDataに同期（ローカル変更を保持）
  useEffect(() => {
    if (mySkillsData) {
      // 原本データを保存
      const originalData: Record<string, EmployeeSkill> = {}
      mySkillsData.forEach(skill => {
        originalData[skill.item_id] = { ...skill }
      })
      setOriginalSkillData(originalData)

      setSkillData(prev => {
        const newSkillData: Record<string, EmployeeSkill> = { ...prev }

        // サーバーから取得したデータで更新
        mySkillsData.forEach(skill => {
          const localSkill = prev[skill.item_id]

          // 以下の場合はサーバーデータを使用:
          // - ローカルに存在しない
          // - ローカルにIDがない(未保存) → サーバーにIDがある場合は既に保存済み
          // - IDが一致する → 同じレコード
          if (!localSkill || !localSkill.id || localSkill.id === skill.id) {
            newSkillData[skill.item_id] = skill
          }
          // ローカルにIDがあり、サーバーと異なる場合のみローカルを保持
        })

        return newSkillData
      })

      // 承認済みスキルと承認待ちスキルは編集不可にする
      setEditableSkills(prev => {
        const newEditableSkills: Record<string, boolean> = { ...prev }
        mySkillsData.forEach(skill => {
          // 承認済みと承認待ち以外は編集可能
          newEditableSkills[skill.item_id] = skill.status !== 'approved' && skill.status !== 'pending_approval'
        })
        return newEditableSkills
      })

      // 編集済みマークをクリア
      setEditedSkillIds(new Set())
    }
  }, [mySkillsData])

  // ==================== 資格情報関連のAPI ====================

  // 資格情報一覧取得
  const { data: certificationsData = [], isLoading: certificationsLoading, refetch: refetchCertifications } = useQuery<EmployeeCertification[]>({
    queryKey: ['employee-certifications'],
    queryFn: async () => {
      const response = await api.get('/employee-certifications')
      return response.data
    },
  })

  // 資格情報作成
  const createCertificationMutation = useMutation({
    mutationFn: async (data: EmployeeCertification) => {
      const response = await api.post('/employee-certifications', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-certifications'] })
      toast.success('資格情報を登録しました')
      handleCloseCertDialog()
    },
    onError: (error: unknown) => {
      applyCertApiError(error, '資格情報の登録に失敗しました')
    },
  })

  // 資格情報更新
  const updateCertificationMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<EmployeeCertification> }) => {
      const response = await api.put(`/employee-certifications/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-certifications'] })
      toast.success('資格情報を更新しました')
      handleCloseCertDialog()
    },
    onError: (error: unknown) => {
      applyCertApiError(error, '資格情報の更新に失敗しました')
    },
  })

  // 資格情報削除
  const deleteCertificationMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/employee-certifications/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-certifications'] })
      toast.success('資格情報を削除しました')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, '資格情報の削除に失敗しました'))
    },
  })

  // 資格情報の無効化（承認済みを最新一覧から外す。監査履歴は保持）
  const invalidateCertificationMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.post(`/employee-certifications/${id}/invalidate`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-certifications'] })
      toast.success('資格情報を無効化しました。同じ資格を再登録できます。')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, '資格情報の無効化に失敗しました'))
    },
  })

  // 資格承認申請（個別）
  const submitCertificationMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await api.post(`/employee-certifications/${id}/submit`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-certifications'] })
      toast.success('承認申請しました')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, '承認申請に失敗しました'))
    },
  })

  // 資格一括承認申請
  const submitAllCertificationsMutation = useMutation({
    mutationFn: async () => {
      const draftCerts = certificationsData.filter(cert =>
        cert.id && (cert.status === 'draft' || cert.status === 'rejected')
      )

      const promises = draftCerts.map(cert =>
        api.post(`/employee-certifications/${cert.id}/submit`).catch(err => ({ error: err, cert }))
      )

      const results = await Promise.allSettled(promises)

      const errors = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && (r.value as any)?.error))
      if (errors.length > 0) {
        throw new Error(`${errors.length}件の申請に失敗しました`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-certifications'] })
      toast.success('一括承認申請しました')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'エラーが発生しました'))
    },
  })

  // ==================== プロジェクト歴関連のAPI ====================

  // プロジェクト一覧取得
  const { data: projectsData = [], isLoading: projectsLoading, refetch: refetchProjects } = useQuery<EmployeeProject[]>({
    queryKey: ['employee-projects'],
    queryFn: async () => {
      const response = await api.get('/employee-projects')
      return response.data
    },
  })

  // プロジェクト作成
  const createProjectMutation = useMutation({
    mutationFn: async (data: EmployeeProject) => {
      const response = await api.post('/employee-projects', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-projects'] })
      toast.success('プロジェクトを登録しました')
      handleCloseProjectDialog()
    },
    onError: (error: unknown) => {
      applyProjectApiError(error, 'プロジェクトの登録に失敗しました')
    },
  })

  // プロジェクト更新
  const updateProjectMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<EmployeeProject> }) => {
      const response = await api.put(`/employee-projects/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-projects'] })
      toast.success('プロジェクトを更新しました')
      handleCloseProjectDialog()
    },
    onError: (error: unknown) => {
      applyProjectApiError(error, 'プロジェクトの更新に失敗しました')
    },
  })

  // プロジェクト削除
  const deleteProjectMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/employee-projects/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-projects'] })
      toast.success('プロジェクトを削除しました')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'プロジェクトの削除に失敗しました'))
    },
  })

  // プロジェクト承認申請（個別）
  const submitProjectMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await api.post(`/employee-projects/${id}/submit`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-projects'] })
      toast.success('承認申請しました')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, '承認申請に失敗しました'))
    },
  })

  // プロジェクト一括承認申請
  const submitAllProjectsMutation = useMutation({
    mutationFn: async () => {
      const draftProjects = projectsData.filter(project =>
        project.id && (project.status === 'draft' || project.status === 'rejected')
      )

      const promises = draftProjects.map(project =>
        api.post(`/employee-projects/${project.id}/submit`).catch(err => ({ error: err, project }))
      )

      const results = await Promise.allSettled(promises)

      const errors = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && (r.value as any)?.error))
      if (errors.length > 0) {
        throw new Error(`${errors.length}件の申請に失敗しました`)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-projects'] })
      toast.success('一括承認申請しました')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'エラーが発生しました'))
    },
  })

  // ==================== スキル関連の処理 ====================

  // カテゴリごとにスキル項目をグループ化
  const groupedSkillItems = useMemo(() => {
    if (!skillItemsData || !categoriesData) return {}
    const grouped: Record<string, SkillItem[]> = {}
    categoriesData.forEach(category => {
      grouped[category.category_id] = skillItemsData.filter(item => item.category_id === category.category_id)
    })
    return grouped
  }, [skillItemsData, categoriesData])

  // フィルタリングされたスキル項目
  const filteredSkillItems = useMemo(() => {
    if (!skillItemsData) return []

    return skillItemsData.filter(item => {
      const skill = skillData[item.item_id]

      if (filterType === 'unentered') {
        return !skill || !skill.skill_level || (skill.skill_level as string) === ''
      } else if (filterType === 'pending') {
        return skill && skill.status === 'pending_approval'
      }
      return true // 'all'
    })
  }, [skillItemsData, skillData, filterType])

  // カテゴリ別の進捗を計算
  const categoryProgress = useMemo(() => {
    if (!categoriesData || !groupedSkillItems) return {}

    const progress: Record<string, { total: number; filled: number; approved: number; pending: number }> = {}

    categoriesData.forEach(category => {
      const items = groupedSkillItems[category.category_id] || []
      const total = items.length
      const filled = items.filter(item => {
        const skill = skillData[item.item_id]
        return skill && skill.skill_level && (skill.skill_level as string) !== ''
      }).length
      const approved = items.filter(item => {
        const skill = skillData[item.item_id]
        return skill && skill.status === 'approved'
      }).length
      const pending = items.filter(item => {
        const skill = skillData[item.item_id]
        return skill && skill.status === 'pending_approval'
      }).length

      progress[category.category_id] = { total, filled, approved, pending }
    })

    return progress
  }, [categoriesData, groupedSkillItems, skillData])

  // スキル情報保存（バッチAPI使用）
  const saveSkillsMutation = useMutation({
    mutationFn: async () => {
      const updates: any[] = []
      const creates: any[] = []

      Object.entries(skillData).forEach(([itemId, skill]) => {
        // 編集されたスキルのみを対象にする
        if (editedSkillIds.has(itemId) && skill.skill_level && (skill.skill_level as string) !== '') {
          if (skill.id) {
            // 既存のスキルを更新（承認済みも含む）
            // 承認済みの場合は、サーバー側でdraftに戻される
            updates.push({
              id: skill.id,
              item_id: skill.item_id,
              skill_level: skill.skill_level,
              comment: skill.comment
            })
          } else {
            // 新規作成
            creates.push({
              item_id: skill.item_id,
              skill_level: skill.skill_level,
              comment: skill.comment
            })
          }
        }
      })

      if (updates.length === 0 && creates.length === 0) {
        throw new Error('編集されたスキルがありません')
      }

      // バッチAPIを使用して一括保存
      const response = await api.post('/skills/employee-skills/batch', {
        creates,
        updates
      })

      return response.data.data
    },
    onSuccess: (data) => {
      // サーバーデータを再取得し、useEffectで自動的に同期される
      queryClient.invalidateQueries({ queryKey: ['my-skills'] })
      toast.success(`${data.created + data.updated}件のスキル情報を保存しました`)
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'エラーが発生しました'))
    },
  })

  // 個別保存
  const saveIndividualSkillMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: { item_id: string; skill_level: string; comment?: string } }) => {
      // デバッグ用：送信データをログ出力
      console.log('=== Save Individual Skill ===');
      console.log('ID:', id);
      console.log('Data:', JSON.stringify(data, null, 2));
      console.log('============================');

      await api.put(`/skills/employee-skills/${id}`, data)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['my-skills'] })
      toast.success('スキル情報を保存しました')
    },
    onError: (error: unknown) => {
      console.error('=== Save Error ===');
      console.error('Error:', error);
      console.error('==================');
      toast.error(extractErrorMessage(error, 'エラーが発生しました'))
    },
  })

  // 個別承認申請
  const submitIndividualSkillMutation = useMutation({
    mutationFn: async ({ skillId, itemId }: { skillId: number; itemId: string }) => {
      await api.post(`/skills/employee-skills/${skillId}/submit`)
      return itemId
    },
    onSuccess: (itemId) => {
      // 申請したスキルの編集マークをクリア
      setEditedSkillIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(itemId)
        return newSet
      })
      queryClient.invalidateQueries({ queryKey: ['my-skills'] })
      toast.success('承認申請を送信しました')
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'エラーが発生しました'))
    },
  })

  // 一括承認申請
  const submitAllForApprovalMutation = useMutation({
    mutationFn: async () => {
      // サーバーの最新データを使用し、編集中のスキルは除外
      const draftSkills = (mySkillsData || []).filter(skill =>
        skill.id &&
        (skill.status === 'draft' || skill.status === 'rejected') &&
        !editedSkillIds.has(skill.item_id) // 編集中のスキルは除外
      )

      if (draftSkills.length === 0) {
        throw new Error('承認申請可能なスキルがありません。編集中のスキルは先に保存してください。')
      }

      const promises = draftSkills.map(skill =>
        api.post(`/skills/employee-skills/${skill.id}/submit`).catch(err => ({ error: err, skill }))
      )

      const results = await Promise.allSettled(promises)

      // エラーがあった場合は集計して返す
      const errors = results.filter(r => r.status === 'rejected' || (r.status === 'fulfilled' && (r.value as any)?.error))
      if (errors.length > 0) {
        throw new Error(`${errors.length}件の申請に失敗しました`)
      }

      return draftSkills.length
    },
    onSuccess: (count) => {
      // 一括申請後は全ての編集マークをクリア
      setEditedSkillIds(new Set())
      queryClient.invalidateQueries({ queryKey: ['my-skills'] })
      toast.success(`${count}件の承認申請を送信しました`)
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'エラーが発生しました'))
    },
  })

  const handleCategoryToggle = (categoryId: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    )
  }

  const handleSkillChange = (itemId: string, field: keyof EmployeeSkill, value: any) => {
    setSkillData(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        item_id: itemId,
        [field]: value
      }
    }))

    // 編集されたスキルをマーク
    setEditedSkillIds(prev => new Set(prev).add(itemId))
  }

  // レベル選択時のハンドラー
  const handleLevelSelect = (itemId: string, level: string, nextItemId?: string) => {
    // 承認済みスキルを編集する場合、自動的に編集可能にする
    const skill = skillData[itemId]
    if (skill?.status === 'approved' && !editableSkills[itemId]) {
      setEditableSkills(prev => ({ ...prev, [itemId]: true }))
      toast.info('承認済みスキルを編集しています。保存後は下書きに戻ります。')
    }

    handleSkillChange(itemId, 'skill_level', level)

    // 次の項目へ自動フォーカス
    if (nextItemId && itemRefs.current[nextItemId]) {
      setTimeout(() => {
        itemRefs.current[nextItemId]?.focus()
      }, 100)
    }
  }

  // コメント追加モーダルを開く
  const handleOpenCommentModal = (itemId: string) => {
    setCurrentEditingItem(itemId)
    setCommentModalOpen(true)
  }

  // コメント保存
  const handleSaveComment = () => {
    setCommentModalOpen(false)
    setCurrentEditingItem(null)
  }

  const handleSaveAllSkills = () => {
    saveSkillsMutation.mutate()
  }

  const handleSubmitAllSkills = () => {
    if (window.confirm('すべての下書きスキルを承認申請しますか？')) {
      submitAllForApprovalMutation.mutate()
    }
  }

  const handleSubmitIndividual = (itemId: string, skillId: number, itemName: string) => {
    if (window.confirm(`「${itemName}」を承認申請しますか？`)) {
      // 編集内容を先に保存してから申請
      const skill = skillData[itemId]
      if (skill && skill.id) {
        saveIndividualSkillMutation.mutate({
          id: skill.id,
          data: {
            item_id: skill.item_id,
            skill_level: skill.skill_level,
            ...(skill.comment && { comment: skill.comment })
          }
        }, {
          onSuccess: () => {
            // 保存成功後に申請を実行
            submitIndividualSkillMutation.mutate({ skillId, itemId })
          }
        })
      }
    }
  }

  const handleSaveIndividual = (itemId: string) => {
    const skill = skillData[itemId]
    if (skill && skill.id) {
      saveIndividualSkillMutation.mutate({
        id: skill.id,
        data: {
          item_id: skill.item_id,
          skill_level: skill.skill_level,
          ...(skill.comment && { comment: skill.comment })
        }
      })
    }
  }

  // ==================== 資格情報関連の処理 ====================

  const handleOpenCertDialog = (certification?: EmployeeCertification) => {
    if (certification) {
      setEditingCertification(certification)
      // 日付フィールドをYYYY-MM-DD形式に変換
      const formattedCert = {
        ...certification,
        acquired_date: toMonthInput(certification.acquired_date),
        expiry_date: certification.expiry_date ? toMonthInput(certification.expiry_date) : null
      }
      setCertFormData(formattedCert)
      setIsCustomCert(certification.is_custom || false)
    } else {
      setEditingCertification(null)
      setCertFormData({
        certification_name: '',
        acquired_date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; })(),
        expiry_date: null,
        certification_number: '',
        issuing_organization: '',
        is_custom: false
      })
      setIsCustomCert(false)
    }
    setCertErrors({})
    setIsCertDialogOpen(true)
  }

  const handleCloseCertDialog = () => {
    setIsCertDialogOpen(false)
    setEditingCertification(null)
    setCertErrors({})
    setCertFormData({
      certification_name: '',
      acquired_date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; })(),
      expiry_date: null,
      certification_number: '',
      issuing_organization: '',
      is_custom: false
    })
    setIsCustomCert(false)
  }

  // 資格フォームのクライアント側必須チェック。問題があればフィールド別エラーと要約トーストを出し false を返す
  const validateCertForm = (): boolean => {
    const errors: Record<string, string> = {}
    if (!certFormData.certification_name) {
      errors.certification_name = '資格名を入力してください'
    }
    if (!certFormData.acquired_date) {
      errors.acquired_date = '取得年月を選択してください'
    }
    setCertErrors(errors)
    if (Object.keys(errors).length > 0) {
      toast.error('入力内容を確認してください')
      return false
    }
    return true
  }

  const handleSaveCertification = () => {
    if (!validateCertForm()) {
      return
    }

    // 年月を月初日に変換して送信
    const submitData = {
      ...certFormData,
      acquired_date: toMonthFirstDay(certFormData.acquired_date),
      expiry_date: toMonthFirstDay(certFormData.expiry_date),
    }

    if (editingCertification?.id) {
      updateCertificationMutation.mutate({ id: editingCertification.id, data: submitData })
    } else {
      createCertificationMutation.mutate({ ...submitData, status: 'draft' })
    }
  }

  const handleDeleteCertification = (id: number) => {
    if (window.confirm('この資格情報を削除してもよろしいですか？')) {
      deleteCertificationMutation.mutate(id)
    }
  }

  const handleInvalidateCertification = (id: number) => {
    if (window.confirm('この承認済み資格を無効化しますか？\n一覧から外れますが、承認履歴は保持されます。同じ資格を再登録できます。')) {
      invalidateCertificationMutation.mutate(id)
    }
  }

  // 差し戻し履歴ダイアログ（承認後も含めて全件表示）
  const [rejectionHistoryOpen, setRejectionHistoryOpen] = useState(false)
  const [rejectionHistoryTitle, setRejectionHistoryTitle] = useState('')
  const [rejectionHistoryLoading, setRejectionHistoryLoading] = useState(false)
  const [rejectionHistoryItems, setRejectionHistoryItems] = useState<RejectionHistoryEntry[]>([])

  const openRejectionHistory = async (
    kind: 'skill' | 'certification' | 'project',
    id: number,
    title: string
  ) => {
    const endpointMap = {
      skill: `/skills/employee-skills/${id}/rejection-history`,
      certification: `/employee-certifications/${id}/rejection-history`,
      project: `/employee-projects/${id}/rejection-history`,
    }
    setRejectionHistoryTitle(title)
    setRejectionHistoryItems([])
    setRejectionHistoryOpen(true)
    setRejectionHistoryLoading(true)
    try {
      const response = await api.get(endpointMap[kind])
      setRejectionHistoryItems(response.data?.data || [])
    } catch (error) {
      toast.error(extractErrorMessage(error, '差し戻し履歴の取得に失敗しました'))
    } finally {
      setRejectionHistoryLoading(false)
    }
  }

  const handleSubmitCertification = (id: number) => {
    if (window.confirm('この資格情報を承認申請してもよろしいですか？')) {
      submitCertificationMutation.mutate(id)
    }
  }

  // 保存して提出（ダイアログから直接提出する場合）
  const handleSaveAndSubmitCertification = async () => {
    if (!validateCertForm()) {
      return
    }

    // 年月を月初日に変換して送信
    const submitData = {
      ...certFormData,
      acquired_date: toMonthFirstDay(certFormData.acquired_date),
      expiry_date: toMonthFirstDay(certFormData.expiry_date),
    }

    try {
      let certId: number

      if (editingCertification?.id) {
        // 既存の資格情報を更新
        const response = await api.put(`/employee-certifications/${editingCertification.id}`, submitData)
        certId = response.data.id
        toast.success('資格情報を更新しました')
      } else {
        // 新規作成
        const response = await api.post('/employee-certifications', { ...submitData, status: 'draft' })
        certId = response.data.id
        toast.success('資格情報を登録しました')
      }

      // データを再取得
      await queryClient.invalidateQueries({ queryKey: ['employee-certifications'] })

      // 承認申請
      await api.post(`/employee-certifications/${certId}/submit`)
      toast.success('承認申請しました')

      // ダイアログを閉じる
      handleCloseCertDialog()

      // データを再取得
      await queryClient.invalidateQueries({ queryKey: ['employee-certifications'] })
    } catch (error) {
      applyCertApiError(error, '処理に失敗しました')
    }
  }

  const handleSubmitAllCertifications = () => {
    if (window.confirm('すべての下書き資格情報を承認申請しますか？')) {
      submitAllCertificationsMutation.mutate()
    }
  }

  // ==================== プロジェクト歴関連の処理 ====================

  const handleOpenProjectDialog = (project?: EmployeeProject) => {
    if (project) {
      setEditingProject(project)
      // 日付フィールドをYYYY-MM-DD形式に変換
      const formattedProject = {
        ...project,
        start_date: toMonthInput(project.start_date),
        end_date: project.end_date ? toMonthInput(project.end_date) : null
      }
      setProjectFormData(formattedProject)
    } else {
      setEditingProject(null)
      setProjectFormData({
        project_name: '',
        start_date: '',
        end_date: null,
        role: '',
        responsibilities: '',
        technologies: '',
        project_description: '',
      })
    }
    setProjectErrors({})
    setIsProjectDialogOpen(true)
  }

  const handleCloseProjectDialog = () => {
    setIsProjectDialogOpen(false)
    setEditingProject(null)
    setProjectErrors({})
    setProjectFormData({
      project_name: '',
      start_date: '',
      end_date: null,
      role: '',
      responsibilities: '',
      technologies: '',
      project_description: '',
    })
  }

  // プロジェクト歴フォームのクライアント側必須・整合チェック。
  // 問題があればフィールド別エラーを設定し、要約トーストを表示して false を返す。
  const validateProjectForm = (): boolean => {
    const errors: Record<string, string> = {}
    if (!projectFormData.project_name) {
      errors.project_name = 'プロジェクト名を入力してください'
    }
    if (!projectFormData.start_date) {
      errors.start_date = '開始年月を選択してください'
    }
    if (
      projectFormData.end_date &&
      projectFormData.start_date &&
      projectFormData.start_date > projectFormData.end_date
    ) {
      errors.end_date = '終了年月は開始年月以降にしてください'
    }
    setProjectErrors(errors)
    if (Object.keys(errors).length > 0) {
      toast.error('入力内容を確認してください')
      return false
    }
    return true
  }

  const handleSaveProject = () => {
    if (!validateProjectForm()) {
      return
    }

    // 送信用データを作成（必要なフィールドのみ、年月は月初日に変換）
    // 任意項目は null ではなく空文字で送信する（フォームUX_バリデーション共通設計方針 §5.3）
    const submitData = {
      project_name: projectFormData.project_name,
      start_date: toMonthFirstDay(projectFormData.start_date),
      end_date: toMonthFirstDay(projectFormData.end_date),
      role: projectFormData.role || '',
      responsibilities: projectFormData.responsibilities || '',
      technologies: projectFormData.technologies || '',
      project_description: projectFormData.project_description || '',
    }

    if (editingProject?.id) {
      updateProjectMutation.mutate({ id: editingProject.id, data: submitData })
    } else {
      createProjectMutation.mutate({ ...submitData, status: 'draft' })
    }
  }

  const handleDeleteProject = (id: number) => {
    if (window.confirm('このプロジェクトを削除してもよろしいですか？')) {
      deleteProjectMutation.mutate(id)
    }
  }

  const handleSubmitProject = (id: number) => {
    if (window.confirm('このプロジェクトを承認申請してもよろしいですか？')) {
      submitProjectMutation.mutate(id)
    }
  }

  // 保存して提出（ダイアログから直接提出する場合）
  const handleSaveAndSubmitProject = async () => {
    if (!validateProjectForm()) {
      return
    }

    // 送信用データを作成（必要なフィールドのみ、年月は月初日に変換）
    // 任意項目は null ではなく空文字で送信する（フォームUX_バリデーション共通設計方針 §5.3）
    const submitData = {
      project_name: projectFormData.project_name,
      start_date: toMonthFirstDay(projectFormData.start_date),
      end_date: toMonthFirstDay(projectFormData.end_date),
      role: projectFormData.role || '',
      responsibilities: projectFormData.responsibilities || '',
      technologies: projectFormData.technologies || '',
      project_description: projectFormData.project_description || '',
    }

    try {
      let projectId: number

      if (editingProject?.id) {
        // 既存のプロジェクト歴を更新
        const response = await api.put(`/employee-projects/${editingProject.id}`, submitData)
        projectId = response.data.data.id
        toast.success('プロジェクト歴を更新しました')
      } else {
        // 新規作成
        const response = await api.post('/employee-projects', { ...submitData, status: 'draft' })
        projectId = response.data.data.id
        toast.success('プロジェクト歴を登録しました')
      }

      // データを再取得
      await queryClient.invalidateQueries({ queryKey: ['employee-projects'] })

      // 承認申請
      await api.post(`/employee-projects/${projectId}/submit`)
      toast.success('承認申請しました')

      // ダイアログを閉じる
      handleCloseProjectDialog()

      // データを再取得
      await queryClient.invalidateQueries({ queryKey: ['employee-projects'] })
    } catch (error) {
      applyProjectApiError(error, '処理に失敗しました')
    }
  }

  const handleSubmitAllProjects = () => {
    if (window.confirm('すべての下書きプロジェクトを承認申請しますか？')) {
      submitAllProjectsMutation.mutate()
    }
  }

  // ==================== 共通ヘルパー ====================

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'draft':
        return <Chip label="下書き" size="small" color="default" />
      case 'pending_approval':
        return <Chip label="承認待ち" size="small" color="warning" />
      case 'approved':
        return <Chip label="承認済み" size="small" color="success" />
      case 'rejected':
        return <Chip label="差し戻し" size="small" color="error" />
      default:
        return null
    }
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
    })
  }

  const isExpiringSoon = (expiryDate: string | null | undefined) => {
    if (!expiryDate) return false
    const expiry = new Date(expiryDate)
    const threeMonthsFromNow = new Date()
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3)
    return expiry <= threeMonthsFromNow && expiry >= new Date()
  }

  const isExpired = (expiryDate: string | null | undefined) => {
    if (!expiryDate) return false
    return new Date(expiryDate) < new Date()
  }

  const getSkillStats = () => {
    const total = (skillItemsData || []).length
    const filled = Object.values(skillData).filter(s => s.skill_level && (s.skill_level as string) !== '').length
    const approved = Object.values(skillData).filter(s => s.status === 'approved').length
    const pending = Object.values(skillData).filter(s => s.status === 'pending_approval').length
    const draft = Object.values(skillData).filter(s => s.status === 'draft' || s.status === 'rejected').length

    return { total, filled, approved, pending, draft }
  }

  const getCertificationStats = () => {
    const total = certificationsData.length
    const approved = certificationsData.filter(c => c.status === 'approved').length
    const pending = certificationsData.filter(c => c.status === 'pending_approval').length
    const draft = certificationsData.filter(c => c.status === 'draft' || c.status === 'rejected').length

    return { total, approved, pending, draft }
  }

  const getProjectStats = () => {
    const total = projectsData.length
    const approved = projectsData.filter(p => p.status === 'approved').length
    const pending = projectsData.filter(p => p.status === 'pending_approval').length
    const draft = projectsData.filter(p => p.status === 'draft' || p.status === 'rejected').length

    return { total, approved, pending, draft }
  }

  const stats = getSkillStats()
  const certStats = getCertificationStats()
  const projectStats = getProjectStats()

  if (skillItemsLoading || mySkillsLoading || certificationsLoading || projectsLoading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>読み込み中...</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" component="h1">
            マイスキル
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PdfIcon />}
            onClick={handleDownloadResume}
          >
            履歴書ダウンロード
          </Button>
        </Box>

        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab icon={<WorkOutlineIcon />} label="スキル登録" />
          <Tab icon={<SchoolIcon />} label="資格登録" />
          <Tab icon={<WorkOutlineIcon />} label="プロジェクト歴" />
        </Tabs>
      </Box>

      {/* スキル統計セクション */}
      {user && <SkillStatsSection employeeId={user.employee_id} />}

      {/* スキル登録タブ */}
      {tabValue === 0 && (
        <Box>
          {/* 統計情報 */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <Alert severity="info">
                <Typography variant="body2">
                  スキル登録状況: {stats.filled}/{stats.total} 項目入力済み
                  （承認済み: {stats.approved}件、承認待ち: {stats.pending}件、下書き: {stats.draft}件）
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                <Tooltip title="サーバーの最新状態を取得します（未保存の入力は破棄されます）">
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      if (
                        editedSkillIds.size > 0 &&
                        !window.confirm('未保存の編集があります。再読込すると入力内容は破棄されます。続行しますか？')
                      ) {
                        return
                      }
                      refetchMySkills()
                    }}
                  >
                    再読込
                  </Button>
                </Tooltip>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveAllSkills}
                  disabled={saveSkillsMutation.isPending}
                >
                  一括保存
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<SendIcon />}
                  onClick={handleSubmitAllSkills}
                  disabled={submitAllForApprovalMutation.isPending || stats.draft === 0}
                >
                  一括申請
                </Button>
              </Box>
            </Grid>
          </Grid>

          {/* フィルタボタン */}
          <Box sx={{ mb: 3 }}>
            <ButtonGroup variant="outlined" size="small">
              <Button
                variant={filterType === 'all' ? 'contained' : 'outlined'}
                onClick={() => setFilterType('all')}
              >
                全て ({skillItemsData?.length || 0})
              </Button>
              <Button
                variant={filterType === 'unentered' ? 'contained' : 'outlined'}
                onClick={() => setFilterType('unentered')}
              >
                未入力のみ ({filteredSkillItems.filter(item => {
                  const skill = skillData[item.item_id]
                  return !skill || !skill.skill_level || (skill.skill_level as string) === ''
                }).length})
              </Button>
              <Button
                variant={filterType === 'pending' ? 'contained' : 'outlined'}
                onClick={() => setFilterType('pending')}
              >
                承認待ち ({Object.values(skillData).filter(s => s.status === 'pending_approval').length})
              </Button>
            </ButtonGroup>
          </Box>

          {/* カテゴリ別進捗バー */}
          <Card sx={{ mb: 3, p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              カテゴリ別進捗
            </Typography>
            <Stack spacing={2}>
              {categoriesData?.map(category => {
                const progress = categoryProgress[category.category_id]
                if (!progress) return null

                const percentage = progress.total > 0 ? (progress.filled / progress.total) * 100 : 0

                return (
                  <Box key={category.category_id}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {category.category_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {progress.filled}/{progress.total}
                        {progress.approved > 0 && ` (承認: ${progress.approved})`}
                        {progress.pending > 0 && ` (待: ${progress.pending})`}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={percentage}
                      sx={{
                        height: 8,
                        borderRadius: 1,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: percentage === 100 ? 'success.main' : 'primary.main'
                        }
                      }}
                    />
                  </Box>
                )
              })}
            </Stack>
          </Card>

          {categoriesData?.map(category => {
            const categoryItems = (groupedSkillItems[category.category_id] || []).filter(item =>
              filteredSkillItems.some(fi => fi.item_id === item.item_id)
            )

            // フィルタ後に項目がない場合はスキップ
            if (categoryItems.length === 0) return null

            const isExpanded = expandedCategories.includes(category.category_id)

            return (
              <Card key={category.category_id} sx={{ mb: 2 }}>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="h6">{category.category_name}</Typography>
                      <Chip
                        label={`${categoryItems.filter(item => skillData[item.item_id]?.skill_level).length}/${categoryItems.length}`}
                        size="small"
                        color={categoryItems.every(item => skillData[item.item_id]?.skill_level) ? 'success' : 'default'}
                      />
                    </Box>
                  }
                  action={
                    <IconButton onClick={(e) => {
                      e.stopPropagation()
                      handleCategoryToggle(category.category_id)
                    }}>
                      <ExpandMoreIcon sx={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }} />
                    </IconButton>
                  }
                  onClick={() => handleCategoryToggle(category.category_id)}
                  sx={{
                    bgcolor: 'grey.100',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'grey.200'
                    }
                  }}
                />
                {isExpanded && (
                  <CardContent>
                    <Stack spacing={1}>
                      {categoryItems.map((item, index) => {
                        const skill = skillData[item.item_id] || { item_id: item.item_id, skill_level: '', comment: null }
                        // 承認済みでも編集可能（編集後はドラフトに戻る）
                        const isEditable = editableSkills[item.item_id] !== false
                        const categoryLevels = levelDefinitionsMap?.[category.category_id] || levelDefinitionsMap?.['default'] || []
                        const nextItemId = index < categoryItems.length - 1 ? categoryItems[index + 1].item_id : undefined

                        return (
                          <Card
                            key={item.item_id}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              bgcolor: skill.status === 'approved' ? 'success.50' :
                                      skill.status === 'pending_approval' ? 'warning.50' :
                                      skill.status === 'rejected' ? 'error.50' : 'background.paper'
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              {/* スキル名 */}
                              <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                                <Typography variant="body2" fontWeight="bold" noWrap>
                                  {item.item_name}
                                </Typography>
                                {item.item_description && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {item.item_description}
                                  </Typography>
                                )}
                              </Box>

                              {/* レベルボタングループ */}
                              <Box sx={{ flex: '0 0 auto' }}>
                                <ButtonGroup size="small">
                                  {categoryLevels.map(levelDef => (
                                    <Tooltip
                                      key={levelDef.level}
                                      title={
                                        <Box>
                                          <Typography variant="caption" fontWeight="bold">{levelDef.level_name}</Typography>
                                          {levelDef.level_description && (
                                            <Typography variant="caption" display="block">{levelDef.level_description}</Typography>
                                          )}
                                        </Box>
                                      }
                                      arrow
                                    >
                                      <span>
                                        <Button
                                          ref={(el) => {
                                            if (levelDef.level === 'D') {
                                              itemRefs.current[item.item_id] = el
                                            }
                                          }}
                                          variant={skill.skill_level === levelDef.level ? 'contained' : 'outlined'}
                                          onClick={() => handleLevelSelect(item.item_id, levelDef.level, nextItemId)}
                                          disabled={!isEditable}
                                          sx={{
                                            minWidth: 40,
                                            bgcolor: skill.skill_level === levelDef.level ? getSkillLevelColor(levelDef.level) : 'transparent',
                                            color: skill.skill_level === levelDef.level ? 'white' : getSkillLevelColor(levelDef.level),
                                            borderColor: getSkillLevelColor(levelDef.level),
                                            '&:hover': {
                                              bgcolor: skill.skill_level === levelDef.level ? getSkillLevelColor(levelDef.level) : `${getSkillLevelColor(levelDef.level)}20`,
                                              borderColor: getSkillLevelColor(levelDef.level),
                                            }
                                          }}
                                        >
                                          {levelDef.level}
                                        </Button>
                                      </span>
                                    </Tooltip>
                                  ))}
                                </ButtonGroup>
                              </Box>

                              {/* 詳細追加ボタン */}
                              <Box sx={{ flex: '0 0 auto' }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleOpenCommentModal(item.item_id)}
                                  disabled={!isEditable}
                                  sx={{ minWidth: 90 }}
                                >
                                  詳細追加
                                </Button>
                              </Box>

                              {/* 個別保存ボタン（編集されたスキルのみ表示、pending_approvalは除外） */}
                              {editedSkillIds.has(item.item_id) && skill.id && skill.skill_level && skill.status !== 'pending_approval' && (
                                <Box sx={{ flex: '0 0 auto' }}>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    startIcon={<SaveIcon />}
                                    onClick={() => handleSaveIndividual(item.item_id)}
                                    disabled={saveIndividualSkillMutation.isPending}
                                    sx={{ minWidth: 90 }}
                                  >
                                    保存
                                  </Button>
                                </Box>
                              )}

                              {/* 個別申請ボタン（draft/rejectedステータスに表示） */}
                              {(skill.status === 'draft' || skill.status === 'rejected') && skill.id && skill.skill_level && (
                                <Box sx={{ flex: '0 0 auto' }}>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<SendIcon />}
                                    onClick={() => handleSubmitIndividual(item.item_id, skill.id!, item.item_name)}
                                    disabled={submitIndividualSkillMutation.isPending}
                                    sx={{ minWidth: 90 }}
                                  >
                                    申請
                                  </Button>
                                </Box>
                              )}

                              {/* ステータスバッジ */}
                              {skill.status && (
                                <Box sx={{ flex: '0 0 auto' }}>
                                  <Chip
                                    label={
                                      skill.status === 'approved' ? '承認済' :
                                      skill.status === 'pending_approval' ? '承認待ち' :
                                      skill.status === 'rejected' ? '差し戻し' :
                                      '下書き'
                                    }
                                    size="small"
                                    color={
                                      skill.status === 'approved' ? 'success' :
                                      skill.status === 'pending_approval' ? 'warning' :
                                      skill.status === 'rejected' ? 'error' :
                                      'default'
                                    }
                                  />
                                </Box>
                              )}

                              {/* 承認済みスキルの編集ボタン */}
                              {skill.status === 'approved' && !editableSkills[item.item_id] && (
                                <Box sx={{ flex: '0 0 auto' }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setEditableSkills(prev => ({ ...prev, [item.item_id]: true }))
                                      toast.info('編集モードになりました。変更後に保存すると下書きに戻ります。')
                                    }}
                                    color="primary"
                                    title="編集（承認済み→下書きに戻ります）"
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Box>
                              )}

                              {/* 差し戻し履歴ボタン（申請済み以降のみ表示） */}
                              {skill.id && skill.status && skill.status !== 'draft' && (
                                <Box sx={{ flex: '0 0 auto' }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => openRejectionHistory('skill', skill.id!, item.item_name)}
                                    title="差し戻し履歴"
                                  >
                                    <HistoryIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>

                            {/* コメントがある場合はプレビュー表示 */}
                            {skill.comment && (
                              <Box sx={{ mt: 1, pl: 1, borderLeft: 2, borderColor: 'primary.main' }}>
                                <Typography variant="caption" color="text.secondary">
                                  {skill.comment.length > 100 ? `${skill.comment.substring(0, 100)}...` : skill.comment}
                                </Typography>
                              </Box>
                            )}

                            {/* 差し戻し理由 */}
                            {skill.status === 'rejected' && skill.rejected_reason && (
                              <Alert severity="error" sx={{ mt: 1, py: 0.5 }}>
                                <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>差し戻し理由: {skill.rejected_reason}</Typography>
                              </Alert>
                            )}
                          </Card>
                        )
                      })}
                    </Stack>
                  </CardContent>
                )}
              </Card>
            )
          })}
        </Box>
      )}

      {/* 資格登録タブ */}
      {tabValue === 1 && (
        <Box>
          {/* 統計情報 */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <Alert severity="info">
                <Typography variant="body2">
                  資格登録状況: {certStats.total}件
                  （承認済み: {certStats.approved}件、承認待ち: {certStats.pending}件、下書き: {certStats.draft}件）
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => refetchCertifications()}
                >
                  再読込
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenCertDialog()}
                >
                  新規登録
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<SendIcon />}
                  onClick={handleSubmitAllCertifications}
                  disabled={submitAllCertificationsMutation.isPending || certStats.draft === 0}
                >
                  一括申請
                </Button>
              </Box>
            </Grid>
          </Grid>

          {certificationsData.length === 0 ? (
            <Alert severity="info">
              資格情報が登録されていません。「新規登録」ボタンから登録してください。
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>資格名</TableCell>
                    <TableCell>取得年月</TableCell>
                    <TableCell>有効期限</TableCell>
                    <TableCell>資格番号</TableCell>
                    <TableCell>発行機関</TableCell>
                    <TableCell>ステータス</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {certificationsData.map((certification) => (
                    <TableRow
                      key={certification.id}
                      sx={{
                        backgroundColor: isExpired(certification.expiry_date)
                          ? 'error.light'
                          : isExpiringSoon(certification.expiry_date)
                          ? 'warning.light'
                          : 'inherit',
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {certification.certification_name}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(certification.acquired_date)}</TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography variant="body2">
                            {formatDate(certification.expiry_date)}
                          </Typography>
                          {isExpired(certification.expiry_date) && (
                            <Chip label="期限切れ" size="small" color="error" />
                          )}
                          {isExpiringSoon(certification.expiry_date) && (
                            <Chip label="更新推奨" size="small" color="warning" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>{certification.certification_number || '-'}</TableCell>
                      <TableCell>{certification.issuing_organization || '-'}</TableCell>
                      <TableCell>
                        <Stack spacing={1}>
                          {getStatusLabel(certification.status)}
                          {certification.status === 'rejected' &&
                            certification.rejected_reason && (
                              <Typography variant="caption" color="error" sx={{ whiteSpace: 'pre-wrap' }}>
                                {certification.rejected_reason}
                              </Typography>
                            )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {(certification.status === 'draft' || certification.status === 'approved') && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenCertDialog(certification)}
                                title={certification.status === 'approved' ? '編集（承認済み→下書きに戻ります）' : '編集'}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              {certification.status === 'draft' && (
                                <>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteCertification(certification.id!)}
                                    title="削除"
                                    color="error"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSubmitCertification(certification.id!)}
                                    title="承認申請"
                                    color="primary"
                                  >
                                    <SendIcon fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                              {certification.status === 'approved' && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleInvalidateCertification(certification.id!)}
                                  title="無効化（一覧から外す・履歴は保持）"
                                  color="error"
                                >
                                  <BlockIcon fontSize="small" />
                                </IconButton>
                              )}
                            </>
                          )}
                          {certification.status === 'rejected' && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenCertDialog(certification)}
                                title="再編集"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleSubmitCertification(certification.id!)}
                                title="承認申請"
                                color="primary"
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                          {certification.id && certification.status && certification.status !== 'draft' && (
                            <IconButton
                              size="small"
                              onClick={() => openRejectionHistory('certification', certification.id!, certification.certification_name)}
                              title="差し戻し履歴"
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* プロジェクト歴タブ */}
      {tabValue === 2 && (
        <Box>
          {/* 統計情報 */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <Alert severity="info">
                <Typography variant="body2">
                  プロジェクト歴登録状況: {projectStats.total}件
                  （承認済み: {projectStats.approved}件、承認待ち: {projectStats.pending}件、下書き: {projectStats.draft}件）
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => refetchProjects()}
                >
                  再読込
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenProjectDialog()}
                >
                  新規登録
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<SendIcon />}
                  onClick={handleSubmitAllProjects}
                  disabled={submitAllProjectsMutation.isPending || projectStats.draft === 0}
                >
                  一括申請
                </Button>
              </Box>
            </Grid>
          </Grid>

          {projectsData.length === 0 ? (
            <Alert severity="info">
              プロジェクト歴が登録されていません。「新規登録」ボタンから登録してください。
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>プロジェクト名</TableCell>
                    <TableCell>期間</TableCell>
                    <TableCell>役割</TableCell>
                    <TableCell>プロジェクト概要</TableCell>
                    <TableCell>使用技術</TableCell>
                    <TableCell>ステータス</TableCell>
                    <TableCell>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {projectsData.map((project) => (
                    <TableRow key={project.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {project.project_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {formatDate(project.start_date)} ~ {project.end_date ? formatDate(project.end_date) : '現在'}
                      </TableCell>
                      <TableCell>
                        {project.role ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {project.role.split(',').map((role, index) => (
                              <Chip
                                key={index}
                                label={role.trim()}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {project.project_description ? (
                          <Typography variant="caption" color="text.secondary">
                            {project.project_description}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{project.technologies || '-'}</TableCell>
                      <TableCell>
                        <Stack spacing={1}>
                          {getStatusLabel(project.status)}
                          {project.status === 'rejected' && project.rejected_reason && (
                            <Typography variant="caption" color="error" sx={{ whiteSpace: 'pre-wrap' }}>
                              {project.rejected_reason}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {(project.status === 'draft' || project.status === 'approved') && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenProjectDialog(project)}
                                title={project.status === 'approved' ? '編集（承認済み→下書きに戻ります）' : '編集'}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              {project.status === 'draft' && (
                                <>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteProject(project.id!)}
                                    title="削除"
                                    color="error"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSubmitProject(project.id!)}
                                    title="承認申請"
                                    color="primary"
                                  >
                                    <SendIcon fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                            </>
                          )}
                          {project.status === 'rejected' && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenProjectDialog(project)}
                                title="再編集"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleSubmitProject(project.id!)}
                                title="承認申請"
                                color="primary"
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                          {project.id && project.status && project.status !== 'draft' && (
                            <IconButton
                              size="small"
                              onClick={() => openRejectionHistory('project', project.id!, project.project_name)}
                              title="差し戻し履歴"
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* 差し戻し履歴ダイアログ（承認後も含めて全件表示） */}
      <Dialog
        open={rejectionHistoryOpen}
        onClose={() => setRejectionHistoryOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>差し戻し履歴{rejectionHistoryTitle ? `：${rejectionHistoryTitle}` : ''}</DialogTitle>
        <DialogContent dividers>
          {rejectionHistoryLoading ? (
            <Box sx={{ py: 2 }}>
              <LinearProgress />
            </Box>
          ) : rejectionHistoryItems.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              差し戻し履歴はありません
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ py: 1 }}>
              {rejectionHistoryItems.map((entry, index) => (
                <Alert key={entry.id} severity="error" icon={false}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
                    <Typography variant="caption" fontWeight="bold">
                      {index + 1}回目の差し戻し
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {entry.rejected_at ? formatDateTimeJST(entry.rejected_at, true) : '-'}
                    </Typography>
                  </Box>
                  {entry.rejected_by_name && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      差し戻し者：{entry.rejected_by_name}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {entry.rejected_reason || '（理由未記入）'}
                  </Typography>
                </Alert>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionHistoryOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* コメント詳細追加モーダル */}
      <Dialog
        open={commentModalOpen}
        onClose={() => setCommentModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>実務経験・補足の詳細入力</DialogTitle>
        <DialogContent>
          {currentEditingItem && (() => {
            const item = skillItemsData?.find(i => i.item_id === currentEditingItem)
            const skill = skillData[currentEditingItem] || { item_id: currentEditingItem, skill_level: '', comment: null }
            const categoryLevels = levelDefinitionsMap?.[item?.category_id || ''] || levelDefinitionsMap?.['default'] || []
            const selectedLevel = categoryLevels.find(def => def.level === skill.skill_level)

            return (
              <Box sx={{ mt: 2 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {item?.item_name}
                  </Typography>
                  {selectedLevel && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" display="block" fontWeight="bold">
                        選択レベル: {selectedLevel.level} - {selectedLevel.level_name}
                      </Typography>
                      {selectedLevel.achievement_criteria && (
                        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                          【達成基準例】{selectedLevel.achievement_criteria}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Alert>

                <TextField
                  label="実務経験・補足"
                  value={skill.comment || ''}
                  onChange={(e) => handleSkillChange(currentEditingItem, 'comment', e.target.value)}
                  fullWidth
                  multiline
                  rows={8}
                  placeholder="プロジェクト名、具体的な業務内容、期間、使用技術、成果などを記載してください。&#10;&#10;例:&#10;・〇〇システム開発プロジェクト (2023年4月〜2024年3月)&#10;・担当業務: フロントエンド開発 (React + TypeScript)&#10;・実績: ユーザー管理画面のリニューアル、パフォーマンス30%改善"
                  helperText="できるだけ具体的に記載することで、承認がスムーズになります"
                />
              </Box>
            )
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentModalOpen(false)}>キャンセル</Button>
          <Button onClick={handleSaveComment} variant="contained" color="primary">
            保存
          </Button>
        </DialogActions>
      </Dialog>

      {/* 資格情報登録・編集ダイアログ */}
      <Dialog open={isCertDialogOpen} onClose={handleCloseCertDialog} maxWidth="md" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle className="draggable-dialog-handle" sx={{ cursor: 'move' }}>
          {editingCertification ? '資格情報編集' : '資格情報新規登録'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* 編集時は資格名を読み取り専用で表示 */}
            {editingCertification ? (
              <>
                <TextField
                  label="資格名"
                  value={certFormData.certification_name}
                  fullWidth
                  margin="normal"
                  disabled
                  helperText="資格名は編集できません"
                />
                <TextField
                  label="発行機関"
                  value={certFormData.issuing_organization || ''}
                  onChange={(e) => setCertFormData({...certFormData, issuing_organization: e.target.value})}
                  fullWidth
                  margin="normal"
                />
              </>
            ) : (
              <>
                {/* カスタム資格切り替えチェックボックス（新規登録時のみ） */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isCustomCert}
                      onChange={(e) => setIsCustomCert(e.target.checked)}
                    />
                  }
                  label="マスタにない資格を入力"
                  sx={{ mb: 2 }}
                />

                {/* カスタム資格の場合は資格名を直接入力、それ以外はマスタから選択 */}
                {isCustomCert ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        資格名
                      </Typography>
                      <RequiredBadge />
                    </Box>
                    <TextField
                      value={certFormData.certification_name}
                      onChange={(e) => setCertFormData({...certFormData, certification_name: e.target.value})}
                      fullWidth
                      error={!!certErrors.certification_name}
                      helperText={certErrors.certification_name}
                    />
                    <TextField
                      label="発行機関"
                      value={certFormData.issuing_organization || ''}
                      onChange={(e) => setCertFormData({...certFormData, issuing_organization: e.target.value})}
                      fullWidth
                      margin="normal"
                    />
                  </>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        資格選択
                      </Typography>
                      <RequiredBadge />
                    </Box>
                    <FormControl fullWidth error={!!certErrors.certification_name}>
                      <Select
                        displayEmpty
                        value={certFormData.certification_id || ''}
                        onChange={(e) => {
                          const selected = certificationMasterData?.find(c => c.certification_id === e.target.value)
                          if (selected) {
                            setCertFormData({
                              ...certFormData,
                              certification_id: selected.certification_id,
                              certification_name: selected.certification_name,
                              issuing_organization: selected.issuing_organization,
                              is_custom: false
                            })
                          }
                        }}
                      >
                        <MenuItem value="" disabled>資格を選択してください</MenuItem>
                        {certificationMasterData?.map(cert => (
                          <MenuItem key={cert.certification_id} value={cert.certification_id}>
                            {cert.certification_name} ({cert.issuing_organization})
                          </MenuItem>
                        ))}
                      </Select>
                      {certErrors.certification_name && (
                        <FormHelperText>{certErrors.certification_name}</FormHelperText>
                      )}
                    </FormControl>
                  </>
                )}
              </>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                取得年月
              </Typography>
              <RequiredBadge />
            </Box>
            <TextField
              type="month"
              value={certFormData.acquired_date}
              onChange={(e) => setCertFormData({...certFormData, acquired_date: e.target.value})}
              fullWidth
              error={!!certErrors.acquired_date}
              helperText={certErrors.acquired_date}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="有効期限"
              type="month"
              value={certFormData.expiry_date || ''}
              onChange={(e) => setCertFormData({...certFormData, expiry_date: e.target.value || null})}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              helperText="有効期限がない場合は空欄"
            />

            <TextField
              label="証明書番号"
              value={certFormData.certification_number || ''}
              onChange={(e) => setCertFormData({...certFormData, certification_number: e.target.value})}
              fullWidth
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Box>
            <Button onClick={handleCloseCertDialog}>キャンセル</Button>
            {editingCertification && (
              <Button
                onClick={() => {
                  if (window.confirm('この資格情報を削除しますか？')) {
                    handleDeleteCertification(editingCertification.id!)
                    handleCloseCertDialog()
                  }
                }}
                color="error"
                sx={{ ml: 1 }}
              >
                削除
              </Button>
            )}
          </Box>
          <Box>
            <Button onClick={handleSaveCertification} variant="contained" sx={{ mr: 1 }}>
              保存
            </Button>
            <Button
              onClick={handleSaveAndSubmitCertification}
              variant="contained"
              color="primary"
            >
              提出
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* プロジェクト登録・編集ダイアログ */}
      <Dialog open={isProjectDialogOpen} onClose={handleCloseProjectDialog} maxWidth="md" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle className="draggable-dialog-handle" sx={{ cursor: 'move' }}>
          {editingProject ? 'プロジェクト編集' : 'プロジェクト新規登録'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    プロジェクト名
                  </Typography>
                  <RequiredBadge />
                </Box>
                <TextField
                  fullWidth
                  value={projectFormData.project_name}
                  onChange={(e) => setProjectFormData({ ...projectFormData, project_name: e.target.value })}
                  error={!!projectErrors.project_name}
                  helperText={projectErrors.project_name}
                />
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    開始年月
                  </Typography>
                  <RequiredBadge />
                </Box>
                <TextField
                  fullWidth
                  type="month"
                  value={projectFormData.start_date}
                  onChange={(e) => setProjectFormData({ ...projectFormData, start_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  error={!!projectErrors.start_date}
                  helperText={projectErrors.start_date}
                />
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    終了年月
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  type="month"
                  value={projectFormData.end_date || ''}
                  onChange={(e) =>
                    setProjectFormData({ ...projectFormData, end_date: e.target.value || null })
                  }
                  InputLabelProps={{ shrink: true }}
                  error={!!projectErrors.end_date}
                  helperText={projectErrors.end_date || '進行中の場合は空欄'}
                />
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                    役割
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {PROJECT_ROLES.map((role) => {
                      const selectedRoles = projectFormData.role ? projectFormData.role.split(',').map(r => r.trim()).filter(r => r) : []
                      const isSelected = selectedRoles.includes(role)
                      return (
                        <Chip
                          key={role}
                          label={role}
                          onClick={() => {
                            const currentRoles = projectFormData.role ? projectFormData.role.split(',').map(r => r.trim()).filter(r => r) : []
                            if (isSelected) {
                              const newRoles = currentRoles.filter(r => r !== role)
                              setProjectFormData({ ...projectFormData, role: newRoles.join(', ') })
                            } else {
                              setProjectFormData({ ...projectFormData, role: [...currentRoles, role].join(', ') })
                            }
                          }}
                          color={isSelected ? 'primary' : 'default'}
                          variant={isSelected ? 'filled' : 'outlined'}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: isSelected ? 'primary.dark' : 'action.hover'
                            }
                          }}
                        />
                      )
                    })}
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="担当業務の詳細"
                  multiline
                  rows={4}
                  value={projectFormData.responsibilities || ''}
                  onChange={(e) =>
                    setProjectFormData({ ...projectFormData, responsibilities: e.target.value })
                  }
                  placeholder="担当した業務の詳細を記載してください"
                  error={!!projectErrors.responsibilities}
                  helperText={projectErrors.responsibilities || '自己アピールポイント、達成した成果（例: パフォーマンス改善30%、ユーザー満足度向上、コスト削減など）、習得したスキル、チーム貢献内容などを具体的に記載してください'}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="使用技術・ツール"
                  value={projectFormData.technologies || ''}
                  onChange={(e) => setProjectFormData({ ...projectFormData, technologies: e.target.value })}
                  placeholder="例: React, TypeScript, Node.js, PostgreSQL, AWS"
                  error={!!projectErrors.technologies}
                  helperText={projectErrors.technologies}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="プロジェクト概要"
                  multiline
                  rows={4}
                  value={projectFormData.project_description || ''}
                  onChange={(e) =>
                    setProjectFormData({ ...projectFormData, project_description: e.target.value })
                  }
                  placeholder="プロジェクトの概要を記載してください"
                  error={!!projectErrors.project_description}
                  helperText={projectErrors.project_description || 'プロジェクトの背景・目的（例: 業務効率化、売上向上、システムリプレース等）、規模（期間、チーム人数）、顧客・業界、課題と解決策などを記載してください'}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Box>
            <Button onClick={handleCloseProjectDialog}>キャンセル</Button>
            {editingProject && (
              <Button
                onClick={() => {
                  if (window.confirm('このプロジェクト歴を削除しますか？')) {
                    handleDeleteProject(editingProject.id!)
                    handleCloseProjectDialog()
                  }
                }}
                color="error"
                sx={{ ml: 1 }}
              >
                削除
              </Button>
            )}
          </Box>
          <Box>
            <Button onClick={handleSaveProject} variant="contained" sx={{ mr: 1 }}>
              保存
            </Button>
            <Button
              onClick={handleSaveAndSubmitProject}
              variant="contained"
              color="primary"
            >
              提出
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default MySkillsPage
